// =============================================================================
// Calculadora de NDF (Non-Deliverable Forward) — replica do "Vinculada a Reuters"
// =============================================================================
// Todas as formulas vivem aqui pra serem reaproveitadas em todas as views
// (Home pra KPIs, Form pra preview ao vivo, Detail pra read-only).
//
// Mapeamento das colunas do form com a planilha:
//   Princ. R$    = princ_usd × spot
//   DU           = NETWORKDAYS(inicio, vencimento, feriados_sp) − 1
//   DC           = vencimento − inicio (dias corridos)
//   Juros BRL PE = (1 + CDI)^(DU/252) − 1
//   Juros USD PE = SOFR × (DC/360)
//   Tx Implicita = (1 + jBRL) / (1 + jUSD) − 1
//   Desv R$      = spot × Tx Implicita
//   Fut Teorico  = spot + Desv R$
//   Tx Premio    = Tx Implicita × (360/DC)
//   Princ Bruto  = ROUND( (1 + Premio)^(DC/360) × Princ R$, 2 )
//   Premio Bruto = Princ Bruto − Princ R$
//   IR           = 20% se DC > 180, senao 22.5% (do Premio Bruto)
//   Premio Liq   = Premio Bruto − IR
//   Total Credito= Princ R$ + Premio Liq + Despesa
//   Taxa Final   = Total Credito / princ_usd
// =============================================================================

// Lista de feriados SP — extraida da aba "Feriados" da planilha original.
// Cobre 2023-2027 (anos relevantes pro historico atual + janela proxima).
// Quando precisar estender, eh so adicionar mais aqui.
export const FERIADOS_SP = new Set([
  // 2023
  '2023-01-01','2023-01-25','2023-02-20','2023-02-21','2023-04-07',
  '2023-04-21','2023-05-01','2023-06-08','2023-07-09','2023-09-07',
  '2023-10-12','2023-11-02','2023-11-15','2023-11-20','2023-12-25',
  // 2024
  '2024-01-01','2024-01-25','2024-02-12','2024-02-13','2024-03-29',
  '2024-04-21','2024-05-01','2024-05-30','2024-07-09','2024-09-07',
  '2024-10-12','2024-11-02','2024-11-15','2024-11-20','2024-12-25',
  // 2025
  '2025-01-01','2025-01-25','2025-03-03','2025-03-04','2025-04-18',
  '2025-04-21','2025-05-01','2025-06-19','2025-07-09','2025-09-07',
  '2025-10-12','2025-11-02','2025-11-15','2025-11-20','2025-12-25',
  // 2026
  '2026-01-01','2026-01-25','2026-02-16','2026-02-17','2026-04-03',
  '2026-04-21','2026-05-01','2026-06-04','2026-07-09','2026-09-07',
  '2026-10-12','2026-11-02','2026-11-15','2026-11-20','2026-12-25',
  // 2027
  '2027-01-01','2027-01-25','2027-02-08','2027-02-09','2027-03-26',
  '2027-04-21','2027-05-01','2027-05-27','2027-07-09','2027-09-07',
  '2027-10-12','2027-11-02','2027-11-15','2027-11-20','2027-12-25',
]);

// NETWORKDAYS — conta dias entre 2 datas excluindo finais de semana
// e feriados. Mesma semantica do Excel.
export function networkdays(iniISO, fimISO, feriados = FERIADOS_SP) {
  if (!iniISO || !fimISO) return 0;
  const d1 = new Date(iniISO + 'T00:00:00');
  const d2 = new Date(fimISO + 'T00:00:00');
  if (d2 < d1) return 0;
  let count = 0;
  const cur = new Date(d1);
  while (cur <= d2) {
    const dow = cur.getDay();
    const iso = cur.toISOString().slice(0, 10);
    if (dow !== 0 && dow !== 6 && !feriados.has(iso)) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

export function diffDays(iniISO, fimISO) {
  if (!iniISO || !fimISO) return 0;
  const d1 = new Date(iniISO + 'T00:00:00');
  const d2 = new Date(fimISO + 'T00:00:00');
  return Math.round((d2 - d1) / 86400000);
}

// Computa TODOS os valores derivados de um fechamento.
// Entrada: objeto cru (cambio_fechamentos row ou form state).
// Saida: objeto enriquecido com todas as colunas derivadas.
export function computar(f) {
  if (!f) return null;
  const out = { ...f };

  // Numericos (DB pode retornar string)
  const usd  = Number(f.princ_usd) || 0;
  const spot = Number(f.spot) || 0;
  const cdi  = Number(f.cdi)  || 0;
  const sofr = Number(f.sofr) || 0;
  const despesa = Number(f.despesa) || 0;

  out.princ_brl = usd * spot;

  out.du = Math.max(0, networkdays(f.dt_inicio, f.dt_vencimento) - 1);
  out.dc = diffDays(f.dt_inicio, f.dt_vencimento);

  out.jrs_brl = out.du > 0 ? Math.pow(1 + cdi, out.du / 252) - 1 : 0;
  out.jrs_usd = out.dc > 0 ? sofr * (out.dc / 360) : 0;

  out.tx_impl  = (1 + out.jrs_usd) !== 0
    ? (1 + out.jrs_brl) / (1 + out.jrs_usd) - 1
    : 0;
  out.desv_impl     = spot * out.tx_impl;
  out.futuro_teorico = spot + out.desv_impl;

  out.tx_premio = out.dc > 0 ? out.tx_impl * (360 / out.dc) : 0;

  out.princ_bruto = out.dc > 0
    ? Math.round(Math.pow(1 + out.tx_premio, out.dc / 360) * out.princ_brl * 100) / 100
    : out.princ_brl;
  out.premio_bruto = Math.round((out.princ_bruto - out.princ_brl) * 100) / 100;

  // IR — 20% se DC > 180, senao 22.5%
  out.ir_aliq = out.dc > 180 ? 0.20 : 0.225;
  out.ir_val  = Math.round(out.premio_bruto * out.ir_aliq * 100) / 100;

  out.premio_liquido = Math.round((out.premio_bruto - out.ir_val) * 100) / 100;
  out.total_credito  = out.princ_brl + out.premio_liquido + despesa;
  out.taxa_final     = usd > 0 ? out.total_credito / usd : 0;

  return out;
}

// =============================================================================
// Formatadores
// =============================================================================
export const fmtBRL = (n, casas = 2) => {
  if (!Number.isFinite(Number(n))) return '—';
  return Number(n).toLocaleString('pt-BR', {
    minimumFractionDigits: casas, maximumFractionDigits: casas,
  });
};

export const fmtUSD = (n, casas = 0) => {
  if (!Number.isFinite(Number(n))) return '—';
  return Number(n).toLocaleString('pt-BR', {
    minimumFractionDigits: casas, maximumFractionDigits: casas,
  });
};

export const fmtPct = (n, casas = 4) => {
  if (!Number.isFinite(Number(n))) return '—';
  return (Number(n) * 100).toLocaleString('pt-BR', {
    minimumFractionDigits: casas, maximumFractionDigits: casas,
  }) + '%';
};

export const fmtDateBR = (iso) => {
  if (!iso) return '—';
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split('-');
  if (!y || !m || !d) return '—';
  return `${d}/${m}/${y}`;
};

export function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

// Parsing de moeda BR (string → number)
export const parseBRL = (s) => {
  if (s === null || s === undefined) return 0;
  return Number(String(s).replace(/\./g, '').replace(',', '.')) || 0;
};
